/**
 * MSAL Authentication Provider
 * Microsoft Authentication Library integration with JWT token support
 */

import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  PopupRequest,
  RedirectRequest,
  InteractionType,
} from '@azure/msal-browser';
import { loginRequest, createLoginRequest, type IdentityProvider } from '../config/msal.config';
import { AuthProvider, type LoginResponse, type RefreshTokenResponse, type TokenIntrospectionResponse } from './authProvider.service';
import { TokenService } from './token.service';
import type { User } from '../context/AuthContext';
import { extractUserFromAccount } from '../utils/entraAccount.utils';
import { acquireTokenSilentDeduplicated } from '../utils/tokenAcquisition.utils';
import { shouldUseMsalRedirect, shouldEnableMsalPopupFallback } from '../utils/featureFlags.utils';
import {
  createRedirectTransactionIfNone,
  clearRedirectTransaction,
  clearRedirectHandled,
  sanitizeReturnTo,
} from '../utils/redirectTransaction.utils';
import { trackRedirectAttempt } from '../utils/authTelemetry.utils';

export interface MsalAuthProviderConfig {
  msalInstance: PublicClientApplication;
  interactionType?: InteractionType;
  scopes?: string[];
}

/**
 * MSAL Authentication Provider
 * Implements Azure AD authentication using Microsoft Authentication Library
 */
export class MsalAuthProvider extends AuthProvider {
  private msalInstance: PublicClientApplication;
  private interactionType: InteractionType;
  private scopes: string[];

  constructor(
    config: MsalAuthProviderConfig,
    tokenService?: TokenService
  ) {
    // Create a minimal config for base class (not used for MSAL)
    super(
      {
        baseUrl: '', // MSAL doesn't use baseUrl
        loginEndpoint: '',
        refreshEndpoint: '',
        logoutEndpoint: '',
        introspectEndpoint: '',
      },
      tokenService
    );

    this.msalInstance = config.msalInstance;
    // Use feature flag if interactionType not explicitly provided
    this.interactionType = config.interactionType ?? (shouldUseMsalRedirect() ? InteractionType.Redirect : InteractionType.Popup);
    this.scopes = config.scopes ?? loginRequest.scopes;
  }

  /**
   * Sign in using MSAL popup (deprecated, fallback only)
   * Kept for emergency rollback when VITE_ENABLE_MSAL_POPUP_FALLBACK=true
   * 
   * @param credentials Optional credentials (not used for MSAL, but kept for interface compatibility)
   * @param idp Optional identity provider to use for sign in
   */
  async login(_credentials?: { email?: string; password?: string }, idp?: IdentityProvider): Promise<LoginResponse> {
    // Phase 6: Popup is deprecated, but kept for fallback
    const usePopupFallback = shouldEnableMsalPopupFallback();
    
    if (!usePopupFallback && this.interactionType === InteractionType.Redirect) {
      throw new Error('Popup login is disabled. Use loginRedirect() instead.');
    }

    try {
      const baseRequest = idp ? createLoginRequest(idp) : loginRequest;
      const request: PopupRequest = {
        ...baseRequest,
        scopes: this.scopes,
      };

      const result = await this.msalInstance.loginPopup(request);
      return await this.handleAuthenticationResult(result);
    } catch (error) {
      console.error('MSAL sign in failed:', error);
      
      // Check for interaction_required or other specific errors
      if (error instanceof Error) {
        if (error.message.includes('interaction_required')) {
          throw new Error('User interaction required. Please try signing in again.');
        }
        if (error.message.includes('user_cancelled') || error.message.includes('user_cancel')) {
          throw new Error('Sign in was cancelled by the user.');
        }
      }
      
      throw new Error(
        error instanceof Error
          ? `MSAL sign in failed: ${error.message}`
          : 'MSAL sign in failed: Unknown error'
      );
    }
  }

  /**
   * Sign in using MSAL redirect
   * 
   * Phase 6: Split API - redirect path returns void (navigation happens automatically).
   * CRITICAL: This is the single chokepoint for redirect initiation (Item 1).
   * 
   * @param idp Optional identity provider to use for sign in
   * @param returnTo Optional path to redirect to after successful login (defaults to /dashboard)
   */
  async loginRedirect(idp?: IdentityProvider, returnTo?: string): Promise<void> {
    try {
      // Phase 6: Must sanitize returnTo
      const sanitizedReturnTo = sanitizeReturnTo(returnTo);
      
      // Phase 6: Single chokepoint - Create txn via createRedirectTransactionIfNone() (best-effort, not atomic)
      const txn = createRedirectTransactionIfNone(sanitizedReturnTo, 'signin', idp);
      if (!txn) {
        throw new Error('Redirect already in progress');
      }
      
      // Phase 7.2: Track redirect attempt (denominator for success rate)
      trackRedirectAttempt(idp, 'signin');
      
      const baseRequest = idp ? createLoginRequest(idp) : loginRequest;
      const request: RedirectRequest = {
        ...baseRequest,
        scopes: this.scopes,
      };
      
      // Initiate redirect - navigation happens automatically
      await this.msalInstance.loginRedirect(request);
      
      // Navigation happens automatically, no return value needed
    } catch (error) {
      console.error('MSAL redirect sign in failed:', error);
      
      // Phase 6: Clear txn/handled on initiation failure
      clearRedirectTransaction();
      clearRedirectHandled();
      
      throw error;
    }
  }

  /**
   * Handle authentication result and extract tokens
   */
  private async handleAuthenticationResult(result: AuthenticationResult): Promise<LoginResponse> {
    const accessToken = result.accessToken;

    // Set active account for future silent token acquisitions
    if (result.account) {
      this.msalInstance.setActiveAccount(result.account);
    }

    // Store access token only
    // PHASE 4: MSAL does NOT store refresh tokens - MSAL uses acquireTokenSilent() for token refresh
    await this.tokenService.setAccessToken(accessToken);
    // Note: We don't store refresh tokens for MSAL - they're not needed since MSAL handles refresh via acquireTokenSilent()

    // Extract user information from account
    const account = result.account;
    const user = this.extractUserFromAccount(account);

    // Calculate expiration time
    const expiresIn = result.expiresOn
      ? Math.floor((result.expiresOn.getTime() - Date.now()) / 1000)
      : 3600; // Default to 1 hour

    return {
      accessToken,
      // PHASE 4: MSAL doesn't use refresh tokens - return empty string (interface requires string)
      // MSAL uses acquireTokenSilent() for token refresh, not refresh tokens
      refreshToken: '',
      expiresIn,
      user,
      tokenType: 'Bearer',
    };
  }

  /**
   * Extract user information from MSAL account
   * Handles both Azure AD and Azure Entra External ID token claims
   * Uses centralized utility for consistent email extraction
   */
  private extractUserFromAccount(account: AccountInfo | null): User {
    return extractUserFromAccount(account);
  }

  /**
   * Refresh access token using MSAL silent token acquisition
   */
  async refreshToken(_refreshTokenValue?: string): Promise<RefreshTokenResponse> {
    try {
      // Set active account if accounts exist but no active account is set
      const accounts = this.msalInstance.getAllAccounts();
      if (!this.msalInstance.getActiveAccount() && accounts.length > 0 && accounts[0]) {
        this.msalInstance.setActiveAccount(accounts[0]);
      }

      const account = this.msalInstance.getActiveAccount();
      if (!account) {
        throw new Error("Active account not set for silent authentication");
      }

      // Use centralized token acquisition with deduplication
      const result = await acquireTokenSilentDeduplicated(
        this.msalInstance,
        account,
        this.scopes
      );

      // Update stored access token only
      // PHASE 4: MSAL does NOT store refresh tokens - MSAL uses acquireTokenSilent() for token refresh
      await this.tokenService.setAccessToken(result.accessToken);
      // Note: We don't store refresh tokens for MSAL - they're not needed since MSAL handles refresh via acquireTokenSilent()

      const expiresIn = result.expiresOn
        ? Math.floor((result.expiresOn.getTime() - Date.now()) / 1000)
        : 3600;

      return {
        accessToken: result.accessToken,
        // PHASE 4: MSAL doesn't use refresh tokens - return empty string (interface requires string)
        // MSAL uses acquireTokenSilent() for token refresh, not refresh tokens
        refreshToken: '',
        expiresIn,
        tokenType: 'Bearer',
      };
    } catch (error) {
      console.error('MSAL token refresh failed:', error);
      
      // If silent acquisition fails with interaction_required, throw specific error
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes('interaction_required') || 
            errorMessage.includes('consent_required') ||
            errorMessage.includes('login_required')) {
          throw new Error('Token refresh requires user interaction. Please sign in again.');
        }
      }
      
      throw new Error(
        error instanceof Error
          ? `Token refresh failed: ${error.message}`
          : 'Token refresh failed: Unknown error'
      );
    }
  }

  /**
   * DEPRECATED: This logout method is no longer used for MSAL.
   * Logout is now handled by LogoutService (modules/auth/services/logout.service.ts).
   * This method is only called for non-MSAL providers via authApi.logout().
   * 
   * @deprecated Use LogoutService for MSAL logout orchestration
   */
  async logout(_accessToken?: string): Promise<void> {
    console.warn('[MsalAuthProvider] logout() called - this should only happen for non-MSAL providers');
    // For REST/OAuth providers, delegate to parent class
    throw new Error('MsalAuthProvider.logout() is deprecated. Use LogoutService instead.');
  }

  /**
   * Introspect token (validate token with MSAL)
   */
  async introspectToken(token: string): Promise<TokenIntrospectionResponse> {
    try {
      // Parse token to get expiration
      const payload = this.tokenService.parseToken(token);
      
      if (!payload) {
        return {
          active: false,
        };
      }

      // Check if token is expired
      const isExpired = this.tokenService.isTokenExpired(token);
      
      return {
        active: !isExpired,
        exp: payload.exp,
        iat: payload.iat,
        sub: payload.sub as string,
        email: payload.email as string,
        name: payload.name as string,
        roles: payload.roles as string[],
      };
    } catch (error) {
      console.error('Token introspection failed:', error);
      return {
        active: false,
      };
    }
  }

  /**
   * Get current account
   */
  getAccount(): AccountInfo | null {
    return this.msalInstance.getActiveAccount();
  }

  /**
   * Handle redirect promise (for redirect flow)
   */
  async handleRedirectPromise(): Promise<AuthenticationResult | null> {
    try {
      const result = await this.msalInstance.handleRedirectPromise();
      return result;
    } catch (error) {
      console.error('Redirect promise handling failed:', error);
      return null;
    }
  }

  /**
   * Get access token silently
   * Attempts silent token acquisition using Azure AD SSO session cookies
   * Uses active account if available, otherwise tries first account from getAllAccounts()
   * If no local account exists, attempts ssoSilent() which can use Azure AD session cookies
   * Does not depend on accounts being shared across portals - each portal checks its own storage
   */
  async getAccessTokenSilently(): Promise<string | null> {
    try {
      // Set active account if accounts exist but no active account is set
      const accounts = this.msalInstance.getAllAccounts();
      if (!this.msalInstance.getActiveAccount() && accounts.length > 0 && accounts[0]) {
        this.msalInstance.setActiveAccount(accounts[0]);
      }

      const account = this.msalInstance.getActiveAccount();
      if (!account) {
        throw new Error("Active account not set for silent authentication");
      }

      // Use centralized token acquisition with deduplication
      const result = await acquireTokenSilentDeduplicated(
        this.msalInstance,
        account,
        this.scopes
      );
      return result.accessToken;
    } catch (error) {
      // Silent auth failed - this is expected if no Azure AD session exists
      // Don't log interaction_required errors as they're normal
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        if (
          !errorMessage.includes('interaction_required') &&
          !errorMessage.includes('consent_required') &&
          !errorMessage.includes('login_required')
        ) {
        }
      }
      return null;
    }
  }
}

/**
 * Create MSAL auth provider instance
 */
export function createMsalAuthProvider(
  msalInstance: PublicClientApplication,
  interactionType?: InteractionType,
  scopes?: string[],
  tokenService?: TokenService
): MsalAuthProvider {
  // Use feature flag if interactionType not explicitly provided
  const defaultInteractionType = shouldUseMsalRedirect() 
    ? InteractionType.Redirect 
    : InteractionType.Popup;
  const finalInteractionType = interactionType ?? defaultInteractionType;
  
  return new MsalAuthProvider(
    {
      msalInstance,
      interactionType: finalInteractionType,
      scopes,
    },
    tokenService
  );
}

